import { useCallback, useEffect, useState } from 'react';
import { useAppConfig } from '../contexts/AppConfigContext';
import { useTenantData } from '../contexts/TenantDataContext';
import { usePermissions } from './usePermissions';
import { getDaysSince } from '../utils/timeUtils';

/** How long the free setup session stays on offer, counted from account creation. */
const OFFER_WINDOW_DAYS = 14;

const dismissKey = (tenantId: string) => `routemize.setupSessionBannerDismissed.${tenantId}`;

const readDismissed = (tenantId: string | null): boolean =>
  !!tenantId && localStorage.getItem(dismissKey(tenantId)) === 'true';

export interface SetupSessionOffer {
  /** True while the free onboarding call should still be pitched to this user. */
  isEligible: boolean;
  /** Days left in the offer window, or null when not eligible. */
  daysRemaining: number | null;
  dismiss: () => void;
}

/**
 * Eligibility for the free onboarding call offer: the roles who actually set the
 * account up — Super Users and Scheduling Assistants — for the first two weeks
 * after the tenant was created, until they dismiss it. Dismissal is persisted per
 * tenant so the same account isn't re-prompted on every login.
 */
export const useSetupSessionOffer = (): SetupSessionOffer => {
  const { config } = useAppConfig();
  const { companySettings } = useTenantData();
  const { roleType } = usePermissions();

  const tenantId = config?.currentUser?.tenantId ?? null;
  const [dismissed, setDismissed] = useState(() => readDismissed(tenantId));

  // Impersonation swaps the tenant under us, so re-read the flag on change.
  useEffect(() => {
    setDismissed(readDismissed(tenantId));
  }, [tenantId]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    if (tenantId) {
      localStorage.setItem(dismissKey(tenantId), 'true');
    }
  }, [tenantId]);

  const accountAgeInDays = getDaysSince(companySettings?.creationTime);
  const isNewAccount =
    accountAgeInDays !== null && accountAgeInDays >= 0 && accountAgeInDays < OFFER_WINDOW_DAYS;
  const canBookSetupSession = roleType === 'super_user' || roleType === 'booking_admin';
  const isEligible = !dismissed && canBookSetupSession && isNewAccount;

  return {
    isEligible,
    daysRemaining: isEligible ? OFFER_WINDOW_DAYS - (accountAgeInDays as number) : null,
    dismiss,
  };
};
